// Map-specific play (§6): what a person learns after one round on each arena.
//  - Crumble Cake: every step cracks the tile underfoot and candle tiles hold until Sudden Death,
//    so between fights a bot holds a candle tile (or the center), away from a flickering candle
//    and from other beans, instead of strolling the cake to pieces; and it hops gaps.
//  - Spin Cycle: the arms are always visible and always coming, so a bot that wants to be left
//    alone rides the gap between two arms, circling the hub with them, instead of hopping every
//    pass (and by the hub, where an arm crawls past too slowly to hop, getting swept).
import { TUNE } from '../constants';
import { BS, MapId, RoundPhase, TileState } from '../ids';
import { CAKE_GRID, CANDLE_TILES, CENTER_TILE, cakeTempo, nextCandleFire, tileWalkable } from '../maps/cake';
import { MAPS } from '../maps/index';
import { createHazardPose } from '../maps/mapTypes';
import { MODES } from '../modes/index';
import type { Vec2 } from '../types';
import { wantDir, wantPoint } from './motor';
import { grounded, type Ctx } from './context';
import { BOT_TUNE } from './tuning';
import { datan2, dcos, dsin } from '../sim/dmath';

const K = BOT_TUNE.cake;
const S = BOT_TUNE.spin;
const TAU = Math.PI * 2;
const POSE_NOW = createHazardPose();
const POSE_AHEAD = createHazardPose();

function wrap(a: number): number {
  const r = a % TAU;
  return r < 0 ? r + TAU : r;
}

/**
 * Spin Cycle (§6.5): a spot to ride the gap between the sweeper arms, chosen in the frame turning
 * with the arms (where they stand still) and handed back in world coordinates `S.leadTicks` ahead
 * so the motor runs with the gap. Candidates are radii × fractions across the bot's current gap
 * (the middle is safest from both arms, which also survives a Sudden Death reversal), scored on
 * edge safety and on distance from enemies' predicted spots; `threatWeight` sets how much the
 * enemies matter (a crown wearer cares most). Writes `out`; false off Spin Cycle or before BONK.
 */
export function spinGapSpot(ctx: Ctx, threatWeight: number, out: Vec2): boolean {
  const w = ctx.w;
  if (w.cfg.mapId !== MapId.SPIN || w.tick < 0) return false;
  const b = ctx.self;
  const map = MAPS[MapId.SPIN];
  map.hazardPose(w, w.tick, POSE_NOW);
  map.hazardPose(w, w.tick + S.leadTicks, POSE_AHEAD);
  const n = Math.min(POSE_NOW.armCount, POSE_AHEAD.armCount);
  if (n < 2) return false;
  // The gap we stand in now: between the arm just below our angle and the one just above it.
  const phi = wrap(datan2(b.pos.y, b.pos.x));
  // Arm i keeps its index between the two poses; find the gap's bounding arms by index.
  let lo = -1;
  let hi = -1;
  let bestLo = TAU;
  let bestHi = TAU;
  for (let i = 0; i < n; i++) {
    const a = wrap(POSE_NOW.armAngles[i]);
    const below = wrap(phi - a);
    const above = wrap(a - phi);
    if (below < bestLo) {
      bestLo = below;
      lo = i;
    }
    if (above < bestHi) {
      bestHi = above;
      hi = i;
    }
  }
  const a0 = POSE_AHEAD.armAngles[lo];
  let width = wrap(POSE_AHEAD.armAngles[hi] - a0);
  if (width < 1e-3) width = TAU;
  const margin = ctx.margin;
  // Out where the gap moves faster than we can run, it would leave us behind.
  const omega = Math.abs(POSE_NOW.armOmega ?? 0);
  const speed = TUNE.bean.runSpeed * MODES[ctx.w.cfg.modeId].speedMult(ctx.w, b.slot);
  const teams = ctx.bc.teamsOn && b.team !== 0;
  let best = -1e9;
  for (let ri = 0; ri < S.radii.length; ri++) {
    const r = S.radii[ri];
    if (ri > 0 && r * omega > speed * S.maxTrackFrac) continue;
    for (let fi = 0; fi < S.fracs.length; fi++) {
      const f = S.fracs[fi];
      const a = a0 + width * f;
      const x = dcos(a) * r;
      const y = dsin(a) * r;
      const safety = ctx.field.safety(x, y);
      if (safety <= 0) continue;
      // Arc clearance to the nearer arm (meters at this radius), capped: the middle of a wide gap.
      const clear = Math.min(f, 1 - f) * width * r;
      let threat = 0;
      for (let k = 0; k < ctx.seen.length; k++) {
        if (k === b.slot || !ctx.seen[k].body || (teams && ctx.w.beans[k].team === b.team)) continue;
        const o = ctx.seen[k];
        const d = Math.hypot(o.x + o.vx * S.threatPredictSec - x, o.y + o.vy * S.threatPredictSec - y);
        threat += 1 / Math.max(S.threatMin, d);
      }
      const score = Math.min(clear, S.clearCap) * S.clearWeight + Math.min(safety, margin + S.safetyCap) * S.safetyWeight
        - threat * threatWeight - Math.hypot(x - b.pos.x, y - b.pos.y) * S.travelWeight;
      if (score > best) {
        best = score;
        out.x = x;
        out.y = y;
      }
    }
  }
  if (best > -1e9) ctx.brain.gapRideTick = w.tick;
  return best > -1e9;
}

function crowdAt(ctx: Ctx, x: number, y: number): number {
  let n = 0;
  for (let s = 0; s < ctx.seen.length; s++) {
    if (s === ctx.self.slot || !ctx.seen[s].body) continue;
    const d = Math.hypot(ctx.seen[s].x - x, ctx.seen[s].y - y);
    if (d < K.crowdRadius) n += 1 - d / K.crowdRadius;
  }
  return n;
}

/**
 * Crumble Cake anchor: the best stable spot to hold between fights (a candle tile, standing beside
 * the candle on the inner side, or the center tile). Writes `out`; false if none is usable.
 */
export function cakeAnchor(ctx: Ctx, out: Vec2): boolean {
  const w = ctx.w;
  if (w.cfg.mapId !== MapId.CAKE) return false;
  const b = ctx.self;
  const br = ctx.brain;
  const st = w.map.tileState;
  const tm = w.map.tileTimer;
  const sd = w.phase === RoundPhase.SUDDEN_DEATH;
  let best = -1e9;
  let bestTile = -1;
  for (let i = 0; i <= CANDLE_TILES.length; i++) {
    const t = i < CANDLE_TILES.length ? CANDLE_TILES[i] : CENTER_TILE;
    if (t < 0 || !tileWalkable(st[t]) || (st[t] === TileState.CRACKING && tm[t] < K.crackVoidTicks)) continue;
    const cx = CAKE_GRID.cx[t];
    const cy = CAKE_GRID.cy[t];
    const r = Math.hypot(cx, cy);
    const x = t === CENTER_TILE || r < 1e-6 ? cx : cx - (cx / r) * K.candleStandOff;
    const y = t === CENTER_TILE || r < 1e-6 ? cy : cy - (cy / r) * K.candleStandOff;
    const mine = br.anchorTile === t ? 1 : 0;
    // A candle's flicker grows over its 1-bar warning; better players read it sooner, and the
    // best know the rotation (one candle per bar) and leave the next ones alone.
    const flame = ctx.hz.candleDanger(x, y);
    let noticed = flame >= 1 - br.p.hazardRespect * K.flameNotice ? flame : 0;
    const plan = K.flamePlanTicks[br.p.difficulty] ?? 0;
    if (i < CANDLE_TILES.length && plan > 0) {
      const fireIn = nextCandleFire(i, w.tick, cakeTempo(w)) - w.tick;
      if (fireIn < plan) noticed = Math.max(noticed, 1 - fireIn / plan);
    }
    let score = -Math.hypot(x - b.pos.x, y - b.pos.y) * K.travelWeight - crowdAt(ctx, x, y) * K.crowdWeight
      - noticed * K.flameWeight - (ctx.anchorClaims[t] - mine) * K.claimWeight;
    if (t === CENTER_TILE && sd) score += K.centerBonus;
    if (mine) score += F_KEEP;
    if (score > best) {
      best = score;
      out.x = x;
      out.y = y;
      bestTile = t;
    }
  }
  br.anchorTile = bestTile;
  return best > -1e9;
}

const F_KEEP = 0.4;

/** Crumble Cake: the anchor farthest from a threat at (ax, ay) (Hot Potato keep-away). */
export function cakeAnchorAway(ctx: Ctx, ax: number, ay: number, out: Vec2): boolean {
  const w = ctx.w;
  const b = ctx.self;
  const st = w.map.tileState;
  const tm = w.map.tileTimer;
  let best = -1e9;
  for (let i = 0; i <= CANDLE_TILES.length; i++) {
    const t = i < CANDLE_TILES.length ? CANDLE_TILES[i] : CENTER_TILE;
    if (t < 0 || !tileWalkable(st[t]) || (st[t] === TileState.CRACKING && tm[t] < K.crackVoidTicks)) continue;
    const cx = CAKE_GRID.cx[t];
    const cy = CAKE_GRID.cy[t];
    const r = Math.hypot(cx, cy);
    const x = t === CENTER_TILE || r < 1e-6 ? cx : cx - (cx / r) * K.candleStandOff;
    const y = t === CENTER_TILE || r < 1e-6 ? cy : cy - (cy / r) * K.candleStandOff;
    const score = Math.min(Math.hypot(x - ax, y - ay), K.awayCap) - Math.hypot(x - b.pos.x, y - b.pos.y) * K.travelWeight
      - crowdAt(ctx, x, y) * K.crowdWeight - ctx.hz.candleDanger(x, y) * K.flameWeight;
    if (score > best) {
      best = score;
      out.x = x;
      out.y = y;
    }
  }
  return best > -1e9;
}

/**
 * Gap hop while running for safety: void just ahead but ground within a jump's reach (Cake holes,
 * a fallen Teacup wedge). Presses jump; returns true if it did.
 */
export function gapHop(ctx: Ctx, dx: number, dy: number): boolean {
  const b = ctx.self;
  if (b.state !== BS.NORMAL || !grounded(b) || b.vz > 0) return false;
  const v = Math.hypot(b.vel.x, b.vel.y);
  const l = Math.hypot(dx, dy);
  if (v < K.gapMinSpeed || l < 1e-6) return false;
  const ux = dx / l;
  const uy = dy / l;
  if ((b.vel.x * ux + b.vel.y * uy) / v < 0.7) return false;
  const f = ctx.field;
  if (f.safety(b.pos.x + ux * K.gapNear, b.pos.y + uy * K.gapNear) > 0) return false;
  const reach = Math.min(K.gapFar, v * 0.55);
  for (let s = reach; s >= K.gapNear + 0.6; s -= 0.4) {
    if (f.safety(b.pos.x + ux * s, b.pos.y + uy * s) > TUNE.bean.radius * 0.6) {
      ctx.brain.press.jump = true;
      return true;
    }
  }
  return false;
}

/**
 * Crumble Cake travel (§6.4 "jumping preserves tiles"): heading somewhere far at full speed, hop
 * (one cracked tile per 3.8 m instead of two) when the landing spot is solid. Presses jump.
 */
export function cakeHop(ctx: Ctx, tx: number, ty: number): void {
  const w = ctx.w;
  const b = ctx.self;
  if (w.cfg.mapId !== MapId.CAKE || b.state !== BS.NORMAL || !grounded(b) || b.vz > 0) return;
  const v = Math.hypot(b.vel.x, b.vel.y);
  if (v < K.hopMinSpeed || Math.hypot(tx - b.pos.x, ty - b.pos.y) < K.hopTravelMin) return;
  const t = (2 * w.rules.jumpVz) / w.rules.gravity;
  const lx = b.pos.x + b.vel.x * t;
  const ly = b.pos.y + b.vel.y * t;
  if (ctx.field.safety(lx, ly) < K.hopLandSafety || ctx.field.isBlocked(lx, ly)) return;
  if (((b.vel.x * (tx - b.pos.x) + b.vel.y * (ty - b.pos.y)) / (v * Math.hypot(tx - b.pos.x, ty - b.pos.y))) < 0.8) return;
  ctx.brain.press.jump = true;
}

/**
 * Crumble Cake travel to (x, y): walk while the straight path stays on the floor; across a single
 * gap of at most `hopMaxGap`, sprint at it and jump at the brink (the island hop); otherwise follow
 * a planned route over the islands (walk legs and hop legs, re-planned every few ticks). Returns
 * false when the spot cannot be reached at all. Holding the spot relaxes the margin so a bot can
 * stand on a lone candle tile.
 */
export function cakeTravel(ctx: Ctx, x: number, y: number, speed: number): boolean {
  const br = ctx.brain;
  const b = ctx.self;
  const f = ctx.field;
  const dx = x - b.pos.x;
  const dy = y - b.pos.y;
  const d = Math.hypot(dx, dy);
  if (d < K.holdRadius) {
    wantPoint(ctx, x, y, speed);
    br.want.arrive = BOT_TUNE.feel.anchorArrive;
    br.want.margin = Math.min(br.want.margin, BOT_TUNE.feel.anchorMargin);
    return true;
  }
  const gap = firstGap(ctx, x, y);
  if (gap === 0) {
    // Floor all the way: walk (a modest margin keeps us off the rims on the way).
    wantPoint(ctx, x, y, speed);
    br.want.arrive = BOT_TUNE.feel.anchorArrive;
    br.want.margin = Math.min(br.want.margin, K.travelMargin);
    return true;
  }
  if (gap > 0) return hopAt(ctx, x, y);
  // No straight way: route over the islands.
  const w = ctx.w;
  if (w.tick - br.routeTick >= K.routeTicks || Math.hypot(x - br.routeGoalX, y - br.routeGoalY) > K.routeGoalSlack) {
    br.routeTick = w.tick;
    br.routeGoalX = x;
    br.routeGoalY = y;
    br.routeOk = ctx.routes.route(f, b.pos.x, b.pos.y, x, y, K.pathVoid + 0.1, K.hopLandSafety, K.hopMaxGap, br.routeLeg);
  }
  if (!br.routeOk) return false;
  const leg = br.routeLeg;
  if (leg.hop) return hopAt(ctx, leg.x, leg.y);
  if (Math.hypot(leg.x - b.pos.x, leg.y - b.pos.y) < K.legArrive) br.routeTick = -1e9;
  wantPoint(ctx, leg.x, leg.y, speed);
  br.want.arrive = 0.2;
  br.want.margin = Math.min(br.want.margin, K.travelMargin);
  return true;
}

/** Straight-line check to (x, y): 0 = floor all the way, > 0 = one hoppable gap, −1 = otherwise. */
function firstGap(ctx: Ctx, x: number, y: number): number {
  const b = ctx.self;
  const f = ctx.field;
  const dx = x - b.pos.x;
  const dy = y - b.pos.y;
  const d = Math.hypot(dx, dy);
  const ux = dx / d;
  const uy = dy / d;
  let g0 = -1;
  let g1 = -1;
  for (let t = K.pathStep; t < d; t += K.pathStep) {
    const v = f.safety(b.pos.x + ux * t, b.pos.y + uy * t) < K.pathVoid;
    if (v && g0 < 0) g0 = t;
    else if (!v && g0 >= 0) {
      g1 = t;
      break;
    }
  }
  if (g0 < 0) return 0;
  if (g1 < 0 || g1 - g0 > K.hopMaxGap) return -1;
  // Only one gap, and the far side holds all the way to the goal.
  for (let t = g1 + K.pathStep; t < d; t += K.pathStep) if (f.safety(b.pos.x + ux * t, b.pos.y + uy * t) < K.pathVoid) return -1;
  return g1 - g0;
}

/** The island hop toward (x, y): full speed straight at the gap, jump at the brink. */
function hopAt(ctx: Ctx, x: number, y: number): boolean {
  const br = ctx.brain;
  const b = ctx.self;
  const f = ctx.field;
  if (b.state !== BS.NORMAL) return false;
  const dx = x - b.pos.x;
  const dy = y - b.pos.y;
  const d = Math.max(1e-6, Math.hypot(dx, dy));
  const ux = dx / d;
  const uy = dy / d;
  let g0 = d;
  for (let t = K.pathStep; t < d; t += K.pathStep) {
    if (f.safety(b.pos.x + ux * t, b.pos.y + uy * t) < K.pathVoid) {
      g0 = t;
      break;
    }
  }
  wantDir(ctx, ux, uy, 1);
  br.want.urgent = true;
  br.want.precise = true;
  br.want.margin = -1;
  br.want.noCrowd = true;
  br.hazardOwned = true;
  const v = b.vel.x * ux + b.vel.y * uy;
  if (grounded(b) && b.vz <= 0 && g0 <= K.brink + (v / 60) * 2 && v >= K.hopMinRunSpeed) {
    br.press.jump = true;
    br.routeTick = -1e9;
    ctx.tele.islandHops++;
  }
  // Too close to the brink to build speed: step back for a run-up.
  if (g0 < K.runUp && v < K.hopMinRunSpeed && grounded(b)) {
    wantPoint(ctx, b.pos.x - ux * K.runUp, b.pos.y - uy * K.runUp, 1);
    br.want.margin = 0;
  }
  return true;
}
